import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  ChatCircle, PaperPlaneTilt, Plus, User, Users, MagnifyingGlass,
  CircleNotch, DotsThree, X, Check
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChatPage() {
  const { token, user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [searchUser, setSearchUser] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchConversations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/chat/conversations`, { headers });
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
      }
    } catch (err) {
      console.error('Failed to fetch conversations');
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/chat/users`, { headers });
      if (res.ok) {
        setUsers(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch users');
    }
  }, [token]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/chat/unread-count`, { headers });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count);
      }
    } catch (err) {
      console.error('Failed to fetch unread count');
    }
  }, [token]);

  const fetchMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    setLoadingMessages(true);
    try {
      const res = await fetch(`${API}/api/chat/conversations/${conversationId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        setSelectedConversation(data.conversation);
        fetchConversations(); // Refresh to update unread counts
      }
    } catch (err) {
      toast.error('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchConversations(), fetchUsers(), fetchUnreadCount()]);
      setLoading(false);
    };
    loadData();

    // Poll for new messages every 5 seconds
    pollIntervalRef.current = setInterval(() => {
      fetchConversations();
      fetchUnreadCount();
      if (selectedConversation) {
        fetchMessages(selectedConversation.id);
      }
    }, 5000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchConversations, fetchUsers, fetchUnreadCount]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const res = await fetch(`${API}/api/chat/messages`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: messageInput.trim(),
          conversation_id: selectedConversation.id
        })
      });

      if (res.ok) {
        setMessageInput('');
        fetchMessages(selectedConversation.id);
      } else {
        toast.error('Failed to send message');
      }
    } catch (err) {
      toast.error('Error sending message');
    } finally {
      setSending(false);
    }
  };

  const startDirectChat = async (userId) => {
    try {
      const res = await fetch(`${API}/api/chat/conversations/direct?recipient_id=${userId}`, {
        method: 'POST',
        headers
      });

      if (res.ok) {
        const conv = await res.json();
        setShowNewChatModal(false);
        fetchConversations();
        fetchMessages(conv.id);
      } else {
        toast.error('Failed to start conversation');
      }
    } catch (err) {
      toast.error('Error starting conversation');
    }
  };

  const createGroupChat = async (e) => {
    e.preventDefault();
    if (!groupName.trim() || selectedUsers.length === 0) {
      toast.error('Please enter a group name and select users');
      return;
    }

    try {
      const res = await fetch(`${API}/api/chat/conversations/group`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: groupName.trim(),
          participant_ids: selectedUsers
        })
      });

      if (res.ok) {
        const conv = await res.json();
        setShowGroupModal(false);
        setGroupName('');
        setSelectedUsers([]);
        fetchConversations();
        fetchMessages(conv.id);
        toast.success('Group created');
      } else {
        toast.error('Failed to create group');
      }
    } catch (err) {
      toast.error('Error creating group');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const getConversationName = (conv) => {
    if (conv.type === 'group') return conv.name;
    const other = conv.participants?.find(p => p.id !== user?.id);
    return other?.name || 'Unknown';
  };

  const getConversationAvatar = (conv) => {
    if (conv.type === 'group') {
      return <Users className="w-6 h-6 text-primary" />;
    }
    const other = conv.participants?.find(p => p.id !== user?.id);
    const initials = other?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
    return <span className="text-sm font-semibold">{initials}</span>;
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString();
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(searchUser.toLowerCase()) ||
    u.email.toLowerCase().includes(searchUser.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <CircleNotch className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-180px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <ChatCircle className="w-7 h-7 text-primary" />
            Messages
            {unreadCount > 0 && (
              <Badge className="bg-red-500 text-white">{unreadCount}</Badge>
            )}
          </h1>
          <p className="text-muted-foreground mt-1">Internal team communication</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowGroupModal(true)}>
            <Users className="w-4 h-4 mr-2" />
            New Group
          </Button>
          <Button onClick={() => setShowNewChatModal(true)} data-testid="new-chat-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Chat
          </Button>
        </div>
      </div>

      {/* Chat Container */}
      <div className="flex-1 flex border border-border rounded-xl overflow-hidden bg-card">
        {/* Conversation List */}
        <div className="w-80 border-r border-border flex flex-col">
          <div className="p-3 border-b border-border">
            <div className="relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search conversations..." className="pl-9" />
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <ChatCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No conversations yet</p>
                <p className="text-sm">Start a chat with a team member</p>
              </div>
            ) : (
              conversations.map(conv => (
                <div
                  key={conv.id}
                  onClick={() => fetchMessages(conv.id)}
                  className={`p-3 border-b border-border cursor-pointer hover:bg-muted/50 transition-colors
                    ${selectedConversation?.id === conv.id ? 'bg-muted' : ''}
                  `}
                  data-testid={`conversation-${conv.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      {getConversationAvatar(conv)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium truncate">{getConversationName(conv)}</h4>
                        {conv.last_message && (
                          <span className="text-xs text-muted-foreground">
                            {formatTime(conv.last_message.created_at)}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {conv.last_message?.content || 'No messages yet'}
                      </p>
                    </div>
                    {conv.unread_count > 0 && (
                      <Badge className="bg-primary text-white">{conv.unread_count}</Badge>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Message Area */}
        <div className="flex-1 flex flex-col">
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    {getConversationAvatar(selectedConversation)}
                  </div>
                  <div>
                    <h3 className="font-semibold">{getConversationName(selectedConversation)}</h3>
                    <p className="text-xs text-muted-foreground">
                      {selectedConversation.type === 'group'
                        ? `${selectedConversation.participants?.length} members`
                        : 'Direct message'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {loadingMessages ? (
                  <div className="flex items-center justify-center h-full">
                    <CircleNotch className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    <div className="text-center">
                      <ChatCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No messages yet</p>
                      <p className="text-sm">Send the first message!</p>
                    </div>
                  </div>
                ) : (
                  messages.map((msg, idx) => {
                    const isOwn = msg.sender_id === user?.id;
                    const showAvatar = idx === 0 || messages[idx - 1].sender_id !== msg.sender_id;
                    
                    return (
                      <motion.div
                        key={msg.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`flex gap-2 max-w-[70%] ${isOwn ? 'flex-row-reverse' : ''}`}>
                          {!isOwn && showAvatar && (
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-semibold">
                                {msg.sender_name?.split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                          )}
                          {!isOwn && !showAvatar && <div className="w-8" />}
                          
                          <div>
                            {!isOwn && showAvatar && (
                              <p className="text-xs text-muted-foreground mb-1">{msg.sender_name}</p>
                            )}
                            <div
                              className={`rounded-2xl px-4 py-2 ${
                                isOwn
                                  ? 'bg-primary text-primary-foreground rounded-br-sm'
                                  : 'bg-muted rounded-bl-sm'
                              }`}
                            >
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            </div>
                            <p className={`text-xs text-muted-foreground mt-1 ${isOwn ? 'text-right' : ''}`}>
                              {formatTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-border">
                <div className="flex gap-2">
                  <Input
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    placeholder="Type a message..."
                    disabled={sending}
                    className="flex-1"
                    data-testid="message-input"
                  />
                  <Button type="submit" disabled={sending || !messageInput.trim()}>
                    {sending ? (
                      <CircleNotch className="w-4 h-4 animate-spin" />
                    ) : (
                      <PaperPlaneTilt className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <ChatCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium">Select a conversation</h3>
                <p className="text-sm">Choose a chat from the list or start a new one</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Chat Modal */}
      <Dialog open={showNewChatModal} onOpenChange={setShowNewChatModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start New Chat</DialogTitle>
            <DialogDescription>Select a team member to chat with</DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="relative mb-4">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                className="pl-9"
                value={searchUser}
                onChange={(e) => setSearchUser(e.target.value)}
              />
            </div>
            
            <div className="max-h-64 overflow-y-auto space-y-2">
              {filteredUsers.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No users found</p>
              ) : (
                filteredUsers.map(u => (
                  <div
                    key={u.id}
                    onClick={() => startDirectChat(u.id)}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-sm font-semibold">
                        {u.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{u.name}</p>
                      <p className="text-sm text-muted-foreground">{u.email}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Group Chat Modal */}
      <Dialog open={showGroupModal} onOpenChange={setShowGroupModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Group Chat</DialogTitle>
            <DialogDescription>Create a group with multiple team members</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={createGroupChat}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Group Name *</Label>
                <Input
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="e.g., Marketing Team"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Select Members</Label>
                <div className="max-h-48 overflow-y-auto space-y-2 border border-border rounded-lg p-2">
                  {users.map(u => (
                    <div
                      key={u.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted cursor-pointer"
                      onClick={() => toggleUserSelection(u.id)}
                    >
                      <Checkbox checked={selectedUsers.includes(u.id)} />
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                        <span className="text-xs font-semibold">
                          {u.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-sm">{u.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {selectedUsers.length > 0 && (
                  <p className="text-sm text-muted-foreground">{selectedUsers.length} members selected</p>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowGroupModal(false)}>Cancel</Button>
              <Button type="submit">Create Group</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
